import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { getMerchantDashboard } from "../../api/merchantDashboardApi";
import ModuleHeader, { ModuleHeaderAction } from "../../components/ModuleHeader/ModuleHeader";
import ModulePageShell from "../../components/ModulePageShell/ModulePageShell";
import "./dashboard.scss";

const titleCase = (value) =>
  String(value || "-").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const getStatusClass = (status) => String(status).toLowerCase().replace(/\s+/g, "-");

const DashboardPage = () => {
  const navigate = useNavigate();
  const { selectedOutlet } = useOutletContext();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const outletId = selectedOutlet?.id || "";

  useEffect(() => {
    setLoading(true);
    setMessage("");
    getMerchantDashboard(outletId)
      .then((result) => {
        setDashboard(result);
        localStorage.setItem("merchant_selected_outlet_id", String(result.selected_outlet_id));
        localStorage.setItem("pickup_waiting_count", String(result.listing.ready_for_pickup));
        window.dispatchEvent(new CustomEvent("pickup-waiting-count-updated", {
          detail: { count: result.listing.ready_for_pickup },
        }));
      })
      .catch((error) => setMessage(error.message))
      .finally(() => setLoading(false));
  }, [outletId]);

  const donut = useMemo(() => {
    const summary = dashboard?.order_status || {};
    const total = Number(summary.paid || 0)
      + Number(summary.ready_for_pickup || 0)
      + Number(summary.collected || 0);
    if (!total) return "conic-gradient(#edf1ef 0deg 360deg)";
    const paidEnd = Number(summary.paid || 0) / total * 360;
    const readyEnd = paidEnd + Number(summary.ready_for_pickup || 0) / total * 360;
    return `conic-gradient(#dff1e7 0deg ${paidEnd}deg, #9fbc1c ${paidEnd}deg ${readyEnd}deg, #0f4f3c ${readyEnd}deg 360deg)`;
  }, [dashboard]);

  if (loading && !dashboard) {
    return (
      <ModulePageShell className="dashboard-page">
        <p className="dashboard-page__feedback">Loading dashboard...</p>
      </ModulePageShell>
    );
  }
  if (message && !dashboard) {
    return (
      <ModulePageShell className="dashboard-page">
        <p className="dashboard-page__feedback">{message}</p>
      </ModulePageShell>
    );
  }

  const listing = dashboard.listing;
  const stats = [
    { label: "Available", value: listing.available, meta: "Today’s listing quantity", icon: "✓" },
    { label: "Sold", value: listing.sold, meta: "Paid orders only", icon: "▦" },
    { label: "Remaining", value: listing.remaining, meta: "Available to purchase", icon: "•" },
    { label: "Collected", value: listing.collected, meta: "Completed pickups", icon: "✓" },
    { label: "Ready for Pickup", value: listing.ready_for_pickup, meta: "Pickup window started", icon: "↝" },
    { label: "Today’s Earnings", value: `RM ${Number(listing.earnings).toFixed(2)}`, meta: "After platform fee", icon: "RM" },
  ];
  const orderStatusSummary = [
    { key: "paid", label: "Paid" },
    { key: "ready_for_pickup", label: "Ready for Pickup" },
    { key: "collected", label: "Collected" },
  ];

  return (
    <ModulePageShell className="dashboard-page">
      {message && <p className="dashboard-page__feedback">{message}</p>}
      <ModuleHeader
        bleed
        eyebrow={`Welcome back, ${dashboard.merchant.name}`}
        title="Manage today's listing, pickup queue, and merchant earnings."
        actions={
          <>
            <ModuleHeaderAction variant="dashboard" onClick={() => navigate("/orders")}>
              View Orders
            </ModuleHeaderAction>
            <ModuleHeaderAction
              variant="dashboard"
              disabled={!listing.product_id}
              onClick={() => listing.product_id && navigate(`/products/${listing.product_id}/edit`)}
            >
              Edit Listing
            </ModuleHeaderAction>
          </>
        }
      />

      <section className="dashboard-listing">
        <div>
          <p className="dashboard-listing__label">Today’s Listing</p>
          <h3>{listing.name}</h3>
          <span>Pickup Window: {listing.pickup_window || "-"}</span>
        </div>
        <button type="button" onClick={() => navigate("/pickup-queue")}>View Pickup Queue</button>
      </section>

      <section className="dashboard-stats" aria-label="Today’s listing summary">
        {stats.map((item) => (
          <article className="dashboard-stat" key={item.label}>
            <div className="dashboard-stat__icon">{item.icon}</div>
            <p>{item.label}</p><h3>{item.value}</h3><span>{item.meta}</span>
          </article>
        ))}
      </section>

      <section className="dashboard-grid">
        <article className="dashboard-card dashboard-card--wide">
          <div className="dashboard-card__header">
            <div><h3>Recent Orders</h3><p>Latest guest checkout orders for your outlet.</p></div>
            <button type="button" onClick={() => navigate("/orders")}>View All</button>
          </div>
          <div className="dashboard-table">
            <div className="dashboard-table__head"><span>Order</span><span>Customer</span><span>Status</span><span>Pickup Window</span><span>Qty</span></div>
            {(dashboard.recent_orders || []).map((order) => (
              <button className="dashboard-table__row" type="button" key={order.id} onClick={() => navigate(`/orders/${order.order_number}`)}>
                <span>{order.order_number}</span>
                <span>{order.customer}</span>
                <span className={`status-pill status-pill--${getStatusClass(titleCase(order.status))}`}>{titleCase(order.status)}</span>
                <span>{order.pickup_window || "-"}</span>
                <span>{order.quantity}</span>
              </button>
            ))}
            {!dashboard.recent_orders?.length && <p className="dashboard-table__empty">No orders for this outlet today.</p>}
          </div>
        </article>

        <article className="dashboard-card">
          <h3>Order Status</h3><p>Today’s distribution</p>
          <div className="dashboard-donut">
            <div className="dashboard-donut__ring" style={{ background: donut }}>
              <div className="dashboard-donut__inner"><strong>{dashboard.fulfillment_rate}%</strong></div>
            </div>
          </div>
          <div className="dashboard-legend-wrapper">
            <div className="dashboard-legend">
              <span><i style={{ background: "#dff1e7" }} />Paid</span>
              <span><i style={{ background: "#9fbc1c" }} />Ready for Pickup</span>
              <span><i style={{ background: "#0f4f3c" }} />Collected</span>
            </div>
          </div>
          {orderStatusSummary.map((item) => (
            <div className="dashboard-summary" key={item.key}>
              <span>{item.label}</span><b>{dashboard.order_status[item.key] || 0}</b>
            </div>
          ))}
        </article>
      </section>
    </ModulePageShell>
  );
};

export default DashboardPage;
